import { useAtomValue } from 'jotai';

import { AssociationBadge } from '@restworld/ui-components';
import { partnerLandingUrl } from '@restworld/utils-common';

import { workerProfessionalAssociationsAtom } from '../global-state.worker-data';
import { CardWrapper } from './card-wrapper';
import { WorkerDataLoader } from './worker-data-loader';
import { useProfileCommonIntl } from './hooks';
import { useFetchWorkerDataLoading } from '../../hooks';

/**
 * The associations the worker has declared, shown as the same badge an employer
 * sees on their candidate card. Rendered as badges rather than a text summary
 * on purpose: the point of the feature is that the worker recognises the mark
 * their association gave them, and a comma-separated list of names does not
 * carry that.
 *
 * When empty it states that plainly instead of raising a "missing information"
 * alert like the other profile sections. Belonging to an association is not an
 * incomplete profile, and nagging someone to join a paid organisation is not
 * something the product should do.
 */
export const ProfessionalAssociationsSection = () => {
  const workerDataLoading = useFetchWorkerDataLoading();
  const associations = useAtomValue(workerProfessionalAssociationsAtom);
  const { translate } = useProfileCommonIntl();

  return (
    <CardWrapper title={translate('common:professional_associations')} editPageType="professional_associations">
      <div id="professional_associations" className="flex flex-col items-start pt-6 w-full">
        {workerDataLoading && <WorkerDataLoader />}
        {!workerDataLoading && associations.length > 0 && (
          <div className="flex flex-row flex-wrap gap-2">
            {associations.map((variable) => (
              <AssociationBadge
                key={variable.id}
                label={variable.label}
                description={variable.description}
                iconUrl={variable.icon}
                linkUrl={partnerLandingUrl(variable.key)}
                linkLabel={translate('common:find_out_more')}
              />
            ))}
          </div>
        )}
        {!workerDataLoading && associations.length === 0 && (
          <p className="m-0 text-sm text-gray-600">{translate('common:professional_associations_empty')}</p>
        )}
      </div>
    </CardWrapper>
  );
};

export default ProfessionalAssociationsSection;
